import { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AccountCircle from '@mui/icons-material/AccountCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CircleIcon from '@mui/icons-material/Circle';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Modal, Portal } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import useMediaQuery from '@mui/material/useMediaQuery';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

import dayjs, { type Dayjs } from 'dayjs';

import { getReportExcelUrl, getReportPdfUrl } from '../features/Realtime';
import { getLanguageCode, temperatureContext } from '../lib/context';
import { downloadFromUrl } from '../lib/download';
import { composeInstant, firstMissingRangeField, isRangeChronological } from '../lib/exportRange';
import { formatUtcOffset } from '../lib/timezone';
import './Export.css';
import Report from './Report';

const MAX_EXPORT_RANGE_DAYS = 31;

function Export(props: any) {
    const { openExport, setOpenExport, records } = props;
    const { t, i18n } = useTranslation(undefined, { keyPrefix: 'RealTime.Export' });

    const [step, setStep] = useState(1);
    const [rangeListOpen, setRangeListOpen] = useState(false);
    const [deviceGroupListOpen, setDeviceGroupListOpen] = useState(false);

    const isMobile = useMediaQuery('(max-width: 768px)');
    const desktopModalSx = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '40rem',
        bgcolor: '#FFFFFF',
        borderRadius: '1rem',
        boxShadow: '0px 4px 25px 0px #EAEE9E9',
        p: 0,
    };
    const mobileModalSx = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '92vw',
        maxWidth: '360px',
        bgcolor: '#FFFFFF',
        borderRadius: '24px',
        boxShadow: '0px 12px 40px rgba(15, 23, 42, 0.25)',
        p: 0,
        maxHeight: '90vh',
        overflow: 'hidden',
    };

    const tempUnit = useContext(temperatureContext);
    const currentLanguage = i18n.language;
    const printRef = useRef<HTMLDivElement>(null);

    const rangeList = [
        { id: 'custom', label: t('custom') },
        { id: 'today', label: t('today') },
        { id: 'yesterday', label: t('yesterday') },
        { id: 'this-week', label: t('thisWeek') },
        { id: 'last-week', label: t('lastWeek') },
        { id: 'this-month', label: t('thisMonth') },
    ];

    const titleList = [t('export'), t('chooseDevice'), t('export')];

    const [startDate, setStartDate] = useState<Dayjs | null>(null);
    const [endDate, setEndDate] = useState<Dayjs | null>(null);
    const [startTime, setStartTime] = useState<Dayjs | null>(null);
    const [endTime, setEndTime] = useState<Dayjs | null>(null);
    const [selectedRange, setSelectedRange] = useState(rangeList[0].id);

    const [checked, setChecked] = useState<string[]>([]);
    const [checkedNames, setCheckedNames] = useState<string[]>([]);

    const [reportData, setReportData] = useState({});
    const [exportName, setExportName] = useState('');
    const [preparing, setPreparing] = useState(false);
    const [rangeError, setRangeError] = useState(false);
    // Which of the four custom-range pickers is forced open, and whether to show
    // the "fill this in" message. Only one picker is open at a time.
    const [openPicker, setOpenPicker] = useState<string | null>(null);
    const [missingRangeError, setMissingRangeError] = useState(false);
    const [orderError, setOrderError] = useState(false);

    const isFinalStep = step === 3;

    // The report export window may not exceed MAX_EXPORT_RANGE_DAYS. Missing/invalid
    // dates aren't a range violation, so they pass here and are handled elsewhere.
    const isRangeWithinLimit = () => {
        const start = composeInstant(startDate, startTime);
        const end = composeInstant(endDate, endTime);
        if (!start || !end) return true;
        return end.diff(start, 'day', true) <= MAX_EXPORT_RANGE_DAYS;
    };

    const guardRange = () => {
        // Order first: a backwards window has a negative length, so it would sail
        // through the length check below and produce a silently empty export.
        if (!isRangeChronological({ startDate, startTime, endDate, endTime })) {
            setOrderError(true);
            return false;
        }
        if (!isRangeWithinLimit()) {
            setRangeError(true);
            return false;
        }
        return true;
    };

    // Warn about the first empty range field, then open its picker so the fix
    // is one tap away. Applies to any range (including presets whose values
    // have been cleared afterwards) — see lib/exportRange.
    const guardCustomRangeComplete = () => {
        const missing = firstMissingRangeField({ startDate, startTime, endDate, endTime });
        if (!missing) return true;
        setMissingRangeError(true);
        setOpenPicker(missing);
        return false;
    };

    const handleClose = () => {
        setOpenExport(false);
        setStep(1);
        setRangeListOpen(false);
        setDeviceGroupListOpen(false);
        // Reset error/picker state too — otherwise reopening the modal after a
        // failed submit reopens the picker on a form the user hasn't touched
        // yet, and (within autoHideDuration) the stale snackbar is still visible.
        setOpenPicker(null);
        setMissingRangeError(false);
        setOrderError(false);
    };

    const handleNext = () => {
        if (step === 1 && !guardCustomRangeComplete()) return;
        if (step === 1 && !guardRange()) return;
        setRangeListOpen(false);
        setDeviceGroupListOpen(false);
        setStep((prev: any) => Math.min(prev + 1, 3));
    };

    const handleBack = () => {
        setRangeListOpen(false);
        setDeviceGroupListOpen(false);
        setStep((prev: any) => Math.max(prev - 1, 1));
    };

    const handleChooseDevice = () => {
        // Same gate as Next: this shortcut also advances past step 1, so an
        // incomplete or backwards custom range would otherwise slip through here
        // and only fail at the export click on step 3.
        if (!guardCustomRangeComplete()) return;
        if (!guardRange()) return;
        setRangeListOpen(false);
        setDeviceGroupListOpen(false);
        setStep(2);
    };

    const applyRange = (rangeId: any) => {
        const now = dayjs();

        // Each preset resolves to an explicit [start, end] pair. Two shapes:
        //   - Current-period presets (today, this-week, this-month) run from
        //     the start of that period to `now`.
        //   - Full-past presets (yesterday, last-week) span the whole previous
        //     period; end is that period's end, not `now`.
        // The previous version used `now.subtract(1, unit)` for the "this-…"
        // presets, which produced a rolling last-24h/7d/30d window that only
        // accidentally coincided with the current calendar period.
        let start;
        let end;
        switch (rangeId) {
            case 'today':
                start = now.startOf('day');
                end = now;
                break;
            case 'yesterday': {
                const yesterday = now.subtract(1, 'day');
                start = yesterday.startOf('day');
                end = yesterday.endOf('day');
                break;
            }
            case 'this-week':
                start = now.startOf('week');
                end = now;
                break;
            case 'last-week': {
                const lastWeek = now.subtract(1, 'week');
                start = lastWeek.startOf('week');
                end = lastWeek.endOf('week');
                break;
            }
            case 'this-month':
                start = now.startOf('month');
                end = now;
                break;
            default:
                return;
        }

        // composeInstant drops seconds (YYYY-MM-DD HH:mm), so for a
        // current-period preset opened in the first minute of that period —
        // e.g. picking `today` at 00:00:30 — `start` and `end` round to the
        // same instant and isRangeChronological trips on its own preset.
        // Nudge `end` forward by a minute in that narrow case.
        if (!end.isAfter(start)) {
            end = start.add(1, 'minute');
        }

        setStartDate(start);
        setEndDate(end);
        setStartTime(start);
        setEndTime(end);
    };

    const handleRangeSelect = (rangeId: any) => {
        setSelectedRange(rangeId);
        setRangeListOpen(false);
        applyRange(rangeId);
    };

    const runAllExcel = async () => {
        if (!guardCustomRangeComplete()) return;
        if (!guardRange()) return;
        setPreparing(true);
        for (const deviceId of checked) {
            let rowData = records.find((record: any) => record.deviceId === deviceId);
            await createExcel(rowData);
            await new Promise((resolve: any) => setTimeout(resolve, 300));
        }
        setPreparing(false);
    };

    const createExcel = async (rowData: any) => {
        if (!startDate || !endDate || !startTime || !endTime) return;
        const timeZone = formatUtcOffset();
        const startDateTime = dayjs(startDate.format('YYYY-MM-DD') + ' ' + startTime.format('HH:mm')).toISOString();
        const endDateTime = dayjs(endDate.format('YYYY-MM-DD') + ' ' + endTime.format('HH:mm')).toISOString();
        try {
            const result = await getReportExcelUrl(
                rowData.deviceId,
                'config',
                timeZone,
                getLanguageCode(currentLanguage),
                true,
                {
                    report_type: 'realtime',
                    start_time: startDateTime,
                    end_time: endDateTime,
                }
            );
            if (!result.url) throw new Error('No download URL in API response');
            const nameOrId = rowData.name || rowData.deviceId;
            const fallbackFileName = `${nameOrId}-data-report-${startDate.format('YYYY-MM-DD')}-to-${endDate.format('YYYY-MM-DD')}.xlsx`;
            await downloadFromUrl(result.url, fallbackFileName);
        } catch (error) {
            console.error('Error downloading realtime Excel:', error);
        }
        await new Promise((resolve: any) => setTimeout(resolve, 300));
    };

    const runAllPDF = async () => {
        if (!guardCustomRangeComplete()) return;
        if (!guardRange()) return;
        setPreparing(true);
        for (const deviceId of checked) {
            let rowData = records.find((record: any) => record.deviceId === deviceId);
            await createPDF(rowData);
            await new Promise((resolve: any) => setTimeout(resolve, 300));
        }
        setPreparing(false);
    };

    const createPDF = async (rowData: any) => {
        if (!startDate || !endDate || !startTime || !endTime) return;
        const deviceId = rowData.deviceId;
        const timeZone = formatUtcOffset();
        const startDateTime = dayjs(startDate.format('YYYY-MM-DD') + ' ' + startTime.format('HH:mm')).toISOString();
        const endDateTime = dayjs(endDate.format('YYYY-MM-DD') + ' ' + endTime.format('HH:mm')).toISOString();
        try {
            const result = await getReportPdfUrl(deviceId, 'config', timeZone, getLanguageCode(currentLanguage), true, {
                report_type: 'realtime',
                start_time: startDateTime,
                end_time: endDateTime,
            });
            if (!result.url) throw new Error('No download URL in API response');
            const nameOrId = rowData.name || deviceId;
            const fallbackFileName = `${nameOrId}-data-report-${startDate.format('YYYY-MM-DD')}-to-${endDate.format('YYYY-MM-DD')}.pdf`;
            await downloadFromUrl(result.url, fallbackFileName);
        } catch (error) {
            console.error('Error downloading realtime PDF:', error);
        }
        await new Promise((resolve: any) => setTimeout(resolve, 300));
    };

    const handleToggle = (deviceId: any) => (event?: any) => {
        const currentIndex = checked.indexOf(deviceId);
        const newChecked = [...checked];
        const newCheckedNames = [...checkedNames];

        if (currentIndex === -1) {
            newChecked.push(deviceId);
            newCheckedNames.push(records.find((item: any) => item.deviceId === deviceId).name);
        } else {
            newChecked.splice(currentIndex, 1);
            newCheckedNames.splice(currentIndex, 1);
        }
        setChecked(newChecked);
        setCheckedNames(newCheckedNames);
    };

    // Rendered through a Portal so the Snackbar anchors to the viewport corner
    // rather than the export modal's transformed Box (a CSS `transform` ancestor
    // would otherwise become the containing block for its fixed positioning).
    const rangeErrorSnackbar = (
        <Portal>
            <Snackbar
                open={rangeError}
                autoHideDuration={5000}
                onClose={(event: any, reason: any) => {
                    if (reason === 'clickaway') return;
                    setRangeError(false);
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <div className='exportModal__rangeError'>
                    <CancelIcon fontSize='small' />
                    <span>{t('maxRangeError')}</span>
                </div>
            </Snackbar>
        </Portal>
    );

    // Same treatment as the range error: portalled so it anchors to the viewport,
    // not the modal's transformed Box.
    const orderErrorSnackbar = (
        <Portal>
            <Snackbar
                open={orderError}
                autoHideDuration={5000}
                onClose={(event, reason) => {
                    if (reason === 'clickaway') return;
                    setOrderError(false);
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <div className='exportModal__rangeError'>
                    <CancelIcon fontSize='small' />
                    <span>{t('rangeOrderError')}</span>
                </div>
            </Snackbar>
        </Portal>
    );

    const missingRangeSnackbar = (
        <Portal>
            <Snackbar
                open={missingRangeError}
                autoHideDuration={5000}
                onClose={(event, reason) => {
                    if (reason === 'clickaway') return;
                    setMissingRangeError(false);
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <div className='exportModal__rangeError'>
                    <CancelIcon fontSize='small' />
                    <span>{t('missingRangeError')}</span>
                </div>
            </Snackbar>
        </Portal>
    );

    if (isMobile) {
        return (
            <Modal
                open={openExport}
                onClose={handleClose}
                aria-labelledby='modal-modal-title'
                aria-describedby='modal-modal-description'
            >
                <Box sx={mobileModalSx} className='exportModal exportModal--mobile'>
                    <div className='exportModal__header'>
                        <button
                            type='button'
                            className='exportModal__iconButton exportModal__iconButton--back'
                            onClick={handleBack}
                            disabled={step === 1}
                        >
                            <KeyboardArrowLeftIcon fontSize='medium' />
                        </button>
                        <div className='exportModal__title'>{titleList[step - 1]}</div>
                        <button
                            type='button'
                            className='exportModal__iconButton exportModal__iconButton--close'
                            onClick={handleClose}
                        >
                            <CloseIcon fontSize='small' />
                        </button>
                    </div>
                    <div className='exportModal__body'>
                        {step !== 2 ? (
                            <div className='exportModal__step exportModal__step--range'>
                                <div className='exportModal__section'>
                                    <button
                                        type='button'
                                        className='exportModal__dropdown'
                                        onClick={() => setRangeListOpen((prev: any) => !prev)}
                                    >
                                        <span>
                                            {rangeList.find((r: any) => r.id === selectedRange)?.label ||
                                                rangeList[0].label}
                                        </span>
                                        {rangeListOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </button>
                                    {rangeListOpen ? (
                                        <ul className='exportModal__dropdownList'>
                                            {rangeList.map((item: any) => (
                                                <li key={item.id}>
                                                    <button type='button' onClick={() => handleRangeSelect(item.id)}>
                                                        {item.label}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : null}
                                </div>
                                {/* One row per instant: date and time side by side, labelled From / To.
                                    Previously the dates shared a row and the times shared another, so the
                                    eye compared the two times to each other and a valid overnight window
                                    looked backwards. */}
                                <span className='exportModal__rowLabel'>{t('from')}</span>
                                <div className='exportModal__row'>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            label=''
                                            format='YYYY-MM-DD'
                                            value={startDate}
                                            onChange={setStartDate}
                                            maxDate={endDate ?? undefined}
                                            open={openPicker === 'startDate'}
                                            onOpen={() => setOpenPicker('startDate')}
                                            onClose={() => setOpenPicker(null)}
                                            slotProps={{
                                                textField: {
                                                    placeholder: 'Start date',
                                                    className: 'exportModal__input exportModal__input--date',
                                                    size: 'small',
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <TimePicker
                                            value={startTime}
                                            onChange={setStartTime}
                                            open={openPicker === 'startTime'}
                                            onOpen={() => setOpenPicker('startTime')}
                                            onClose={() => setOpenPicker(null)}
                                            slotProps={{
                                                textField: {
                                                    placeholder: 'Start time',
                                                    className: 'exportModal__input exportModal__input--time',
                                                    size: 'small',
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                </div>
                                <span className='exportModal__rowLabel'>{t('to')}</span>
                                <div className='exportModal__row'>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            label=''
                                            format='YYYY-MM-DD'
                                            value={endDate}
                                            onChange={setEndDate}
                                            minDate={startDate ?? undefined}
                                            open={openPicker === 'endDate'}
                                            onOpen={() => setOpenPicker('endDate')}
                                            onClose={() => setOpenPicker(null)}
                                            slotProps={{
                                                textField: {
                                                    placeholder: 'End date',
                                                    className: 'exportModal__input exportModal__input--date',
                                                    size: 'small',
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <TimePicker
                                            value={endTime}
                                            onChange={setEndTime}
                                            open={openPicker === 'endTime'}
                                            onOpen={() => setOpenPicker('endTime')}
                                            onClose={() => setOpenPicker(null)}
                                            slotProps={{
                                                textField: {
                                                    placeholder: 'End time',
                                                    className: 'exportModal__input exportModal__input--time',
                                                    size: 'small',
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                </div>
                                <div className='exportModal__divider'>
                                    <ExpandMoreIcon fontSize='small' />
                                </div>
                                <button
                                    type='button'
                                    className='exportModal__deviceShortcut'
                                    onClick={handleChooseDevice}
                                >
                                    <SearchIcon fontSize='medium' />
                                    <span>{titleList[1]}</span>
                                </button>
                                {isFinalStep ? (
                                    <div className='exportModal__deviceList'>
                                        {checked.map((deviceId: any, index: any) => {
                                            const selectedRecord = records.find(
                                                (item: any) => item.deviceId === deviceId
                                            );
                                            const selectedName =
                                                selectedRecord?.name ?? checkedNames[index] ?? deviceId;

                                            return (
                                                <div key={deviceId} className='exportModal__deviceItem'>
                                                    <div className='exportModal__deviceMeta'>
                                                        <span className='exportModal__deviceName'>{selectedName}</span>
                                                        <span className='exportModal__deviceId'>ID: {deviceId}</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : null}
                            </div>
                        ) : (
                            <div className='exportModal__step exportModal__step--devices'>
                                <div className='exportModal__section'>
                                    <button
                                        type='button'
                                        className='exportModal__dropdown exportModal__dropdown--secondary'
                                        onClick={() => setDeviceGroupListOpen((prev: any) => !prev)}
                                    >
                                        <span>{t('allDevices')}</span>
                                        {deviceGroupListOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </button>
                                    {deviceGroupListOpen ? (
                                        <ul className='exportModal__dropdownList'>
                                            <li>
                                                <button type='button' onClick={() => setDeviceGroupListOpen(false)}>
                                                    {t('allDevices')}
                                                </button>
                                            </li>
                                        </ul>
                                    ) : null}
                                </div>
                                <TextField
                                    label=''
                                    variant='outlined'
                                    placeholder={t('searchPlaceholder')}
                                    className='exportModal__input exportModal__input--search'
                                    InputProps={{
                                        sx: { height: 40, borderRadius: '24px' },
                                        startAdornment: (
                                            <InputAdornment position='start'>
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <div className='exportModal__deviceList'>
                                    {records.map((item: any, index: any) => (
                                        <div
                                            key={index}
                                            className='exportModal__deviceItem'
                                            onClick={() => handleToggle(item.deviceId)()}
                                            role='button'
                                            tabIndex={0}
                                            onKeyDown={(event: any) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    handleToggle(item.deviceId)();
                                                }
                                            }}
                                        >
                                            <div className='exportModal__deviceMeta'>
                                                <span className='exportModal__deviceName'>{item.name}</span>
                                                <span className='exportModal__deviceId'>ID: {item.deviceId}</span>
                                            </div>
                                            <Checkbox
                                                checked={checked.includes(item.deviceId)}
                                                onChange={(event: any) => {
                                                    event.stopPropagation();
                                                    handleToggle(item.deviceId)(event);
                                                }}
                                                icon={<CircleOutlinedIcon />}
                                                checkedIcon={<CircleIcon style={{ color: '#3F8EE8' }} />}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className='exportModal__footer'>
                        <button
                            type='button'
                            className='exportModal__action exportModal__action--ghost'
                            onClick={handleClose}
                        >
                            {t('cancel')}
                        </button>
                        {isFinalStep ? (
                            <div className='exportModal__footerActions'>
                                <button
                                    type='button'
                                    className='exportModal__action exportModal__action--primary'
                                    style={{ width: '120px', flex: '0 0 120px' }}
                                    onClick={runAllExcel}
                                >
                                    CSV
                                </button>
                                <button
                                    type='button'
                                    className='exportModal__action exportModal__action--primary'
                                    style={{ width: '120px', flex: '0 0 120px' }}
                                    onClick={runAllPDF}
                                >
                                    PDF
                                </button>
                            </div>
                        ) : (
                            <button
                                type='button'
                                className='exportModal__action exportModal__action--primary'
                                onClick={handleNext}
                            >
                                {t('next')}
                            </button>
                        )}
                    </div>
                    <Portal>
                        <Snackbar
                            open={preparing}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            sx={{
                                backgroundColor: '#333333',
                                color: '#FFFFFF',
                                padding: '0 1rem',
                            }}
                        >
                            <p>
                                <CircularProgress size='1rem' style={{ verticalAlign: 'text-top' }} /> Preparing to
                                download {exportName}
                            </p>
                        </Snackbar>
                    </Portal>
                    {rangeErrorSnackbar}
                    {missingRangeSnackbar}
                    {orderErrorSnackbar}
                    <div
                        id='report'
                        ref={printRef}
                        style={{ display: 'block', width: '595pt', position: 'absolute', left: '-2000px' }}
                    >
                        <Report reportData={reportData} />
                    </div>
                </Box>
            </Modal>
        );
    }

    return (
        <Modal
            open={openExport}
            onClose={handleClose}
            aria-labelledby='modal-modal-title'
            aria-describedby='modal-modal-description'
        >
            <Box sx={desktopModalSx} className='exportModal'>
                <div
                    className='button__container'
                    style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'relative',
                        height: '3rem',
                        margin: '1rem 0rem',
                    }}
                >
                    <button
                        className='left'
                        onClick={handleBack}
                        style={{
                            // float: 'left',
                            visibility: step !== 1 ? 'visible' : 'hidden',
                            position: 'absolute',
                            left: '2rem',
                            width: '2rem',
                            height: '2rem',
                        }}
                    >
                        <KeyboardArrowLeftIcon style={{ fontSize: '1.5rem', color: '#757575' }} />
                    </button>
                    <div
                        style={{
                            fontSize: '1.5rem',
                            fontWeight: '600',
                            color: '#53545C',
                            textAlign: 'center',
                            width: '100%',
                        }}
                    >
                        {titleList[step - 1]}
                    </div>
                    <button
                        className='close'
                        onClick={handleClose}
                        style={{
                            position: 'absolute',
                            right: '2rem',
                            width: '2rem',
                            height: '2rem',
                            fontSize: '1.5rem',
                            color: '#757575',
                        }}
                    >
                        &times;
                    </button>
                </div>
                <div
                    className='modal_content'
                    style={{
                        display: step !== 2 ? 'flex' : 'none',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                    }}
                >
                    <div className='rangebar__container__item' style={{ width: '24rem', height: '2rem' }}>
                        <table
                            className=''
                            style={{
                                width: '100%',
                                borderRadius: '2.5rem 2.5rem 2rem 2rem',
                                borderLeft: rangeListOpen ? '1px solid #DFE1E6' : 'none',
                                borderRight: rangeListOpen ? '1px solid #DFE1E6' : 'none',
                                borderBottom: rangeListOpen ? '1px solid #DFE1E6' : 'none',
                                borderTop: rangeListOpen ? 'none' : 'none',
                                background: '#FFFFFF',
                                position: 'relative',
                                zIndex: 1,
                                padding: '0rem 0.5rem 0.5rem 0.5rem',
                            }}
                        >
                            <tr>
                                <td
                                    style={{
                                        height: '2rem',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        borderRadius: '2rem',
                                        border: '1px solid #DFE1E6',
                                        zIndex: 2,
                                        position: 'relative',
                                    }}
                                >
                                    <div
                                        style={{
                                            flexGrow: 1,
                                            textAlign: 'center',
                                            fontSize: '1.25rem',
                                            fontWeight: '600',
                                            fontFamily: 'Roboto',
                                            color: '#53545C',
                                        }}
                                    >
                                        {rangeList.find((r: any) => r.id === selectedRange)?.label ||
                                            rangeList[0].label}
                                    </div>
                                    <button
                                        style={{
                                            width: '2rem',
                                            height: '2rem',
                                            position: 'absolute',
                                            right: '1rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                        onClick={() => setRangeListOpen(!rangeListOpen)}
                                    >
                                        {rangeListOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </button>
                                </td>
                            </tr>
                            {rangeListOpen
                                ? rangeList.map((option: any) => {
                                      // console.log(id)
                                      return (
                                          <>
                                              {/* <div className='hline'></div> */}
                                              <tr>
                                                  <td style={{ textAlign: 'center' }}>
                                                      <button
                                                          style={{
                                                              borderRadius: '2rem',
                                                              border: '1px solid #DFE1E6',
                                                              textAlign: 'center',
                                                              fontSize: '1rem',
                                                              fontWeight: '600',
                                                              fontFamily: 'Roboto',
                                                              color: '#53545C',
                                                          }}
                                                          key={option.id}
                                                          onClick={() => handleRangeSelect(option.id)}
                                                      >
                                                          {option.label}
                                                      </button>
                                                  </td>
                                              </tr>
                                          </>
                                      );
                                  })
                                : null}
                        </table>
                    </div>
                    {/* One row per instant: date and time side by side, labelled From / To.
                        Previously the dates shared a row and the times shared another, so the eye
                        compared the two times to each other and a valid overnight window looked
                        backwards. Border radii follow the swap: each row is one joined control. */}
                    <span className='exportModal__rowLabel exportModal__rowLabel--wide'>{t('from')}</span>
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: '0.25rem' }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={startDate}
                                onChange={(newDate: any) => setStartDate(newDate)}
                                maxDate={endDate ?? undefined}
                                open={openPicker === 'startDate'}
                                onOpen={() => setOpenPicker('startDate')}
                                onClose={() => setOpenPicker(null)}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        sx: {
                                            width: '30%',
                                            '& .MuiOutlinedInput-root': { borderRadius: '2rem 0rem 0rem 2rem' },
                                        },
                                    },
                                }}
                            />
                        </LocalizationProvider>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <TimePicker
                                value={startTime}
                                onChange={(newTime: any) => setStartTime(newTime)}
                                open={openPicker === 'startTime'}
                                onOpen={() => setOpenPicker('startTime')}
                                onClose={() => setOpenPicker(null)}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        sx: {
                                            width: '30%',
                                            '& .MuiOutlinedInput-root': { borderRadius: '0rem 2rem 2rem 0rem' },
                                        },
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </div>
                    <span className='exportModal__rowLabel exportModal__rowLabel--wide'>{t('to')}</span>
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: '0.25rem' }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={endDate}
                                onChange={(newDate: any) => setEndDate(newDate)}
                                minDate={startDate ?? undefined}
                                open={openPicker === 'endDate'}
                                onOpen={() => setOpenPicker('endDate')}
                                onClose={() => setOpenPicker(null)}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        sx: {
                                            width: '30%',
                                            '& .MuiOutlinedInput-root': { borderRadius: '2rem 0rem 0rem 2rem' },
                                        },
                                    },
                                }}
                            />
                        </LocalizationProvider>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <TimePicker
                                value={endTime}
                                onChange={(newTime: any) => setEndTime(newTime)}
                                open={openPicker === 'endTime'}
                                onOpen={() => setOpenPicker('endTime')}
                                onClose={() => setOpenPicker(null)}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        sx: {
                                            width: '30%',
                                            '& .MuiOutlinedInput-root': { borderRadius: '0rem 2rem 2rem 0rem' },
                                        },
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </div>
                    <ExpandMoreIcon
                        style={{ marginTop: '1rem', marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}
                    />
                    <button
                        style={{
                            width: '60%',
                            borderRadius: '2rem',
                            border: '1px solid #DFE1E6',
                            background: '#FFF',
                            padding: step === 1 ? '2rem 0rem' : '0.5rem 0rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            cursor: 'pointer',
                            fontSize: '1rem',
                            fontWeight: '600',
                            fontFamily: 'Roboto',
                            color: '#53545C',
                            marginBottom: step === 1 ? '3rem' : '0rem',
                        }}
                        onClick={handleChooseDevice}
                    >
                        <SearchIcon />
                        {t('chooseDevice')}
                    </button>
                    <div
                        className='device__list'
                        style={{
                            width: '60%',
                            height: '14rem',
                            overflowY: 'auto',
                            marginTop: '1rem',
                            boxSizing: 'border-box',
                            border: '1px solid #DFE1E6',
                            borderRadius: '2rem',
                            background: '#FFFFFF',
                            padding: '1rem',
                            marginBottom: '1rem',
                            display: step === 3 ? 'inline' : 'none',
                        }}
                    >
                        {checked.map((deviceId: any, index: any) => (
                            <div
                                key={index}
                                className='device__item'
                                style={{
                                    width: '100%',
                                    height: '2.5rem',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0px 0px 8px 0px rgba(234, 233, 233, 0.80)',
                                    borderRadius: '62rem',
                                    position: 'relative',
                                    marginBottom: '0.5rem',
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: '1.25rem',
                                        fontFamily: 'Roboto',
                                        fontWeight: '400',
                                        color: '#53545C',
                                    }}
                                >
                                    {checkedNames[index]}
                                </span>
                                <span
                                    style={{
                                        fontSize: '0.75rem',
                                        fontFamily: 'Roboto',
                                        fontWeight: '300',
                                        color: '#757575',
                                    }}
                                >
                                    ID: {deviceId}
                                </span>
                                {/* <button style={{ position: 'absolute', right: '1rem', background: 'none', border: 'none', cursor: 'pointer' }}>
                                        <CloseIcon style={{ color: '#DFE1E6' }} />
                                    </button> */}
                            </div>
                        ))}
                    </div>
                </div>
                <div
                    className='modal_content'
                    style={{
                        display: step === 2 ? 'flex' : 'none',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                    }}
                >
                    <div className='rangebar__container__item' style={{ width: '24rem', height: '2rem' }}>
                        <table
                            className=''
                            style={{
                                width: '100%',
                                borderRadius: '2.5rem 2.5rem 2rem 2rem',
                                borderLeft: rangeListOpen ? '1px solid #DFE1E6' : 'none',
                                borderRight: rangeListOpen ? '1px solid #DFE1E6' : 'none',
                                borderBottom: rangeListOpen ? '1px solid #DFE1E6' : 'none',
                                borderTop: rangeListOpen ? 'none' : 'none',
                                background: '#FFFFFF',
                                position: 'relative',
                                zIndex: 1,
                                padding: '0rem 0.5rem 0.5rem 0.5rem',
                            }}
                        >
                            <tr>
                                <td
                                    style={{
                                        height: '2rem',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        borderRadius: '2rem',
                                        border: '1px solid #DFE1E6',
                                        zIndex: 2,
                                        position: 'relative',
                                    }}
                                >
                                    <div
                                        style={{
                                            flexGrow: 1,
                                            textAlign: 'center',
                                            fontSize: '1.25rem',
                                            fontWeight: '600',
                                            fontFamily: 'Roboto',
                                            color: '#53545C',
                                        }}
                                    >
                                        {t('allDevices')}
                                    </div>
                                    <button
                                        style={{
                                            width: '2rem',
                                            height: '2rem',
                                            position: 'absolute',
                                            right: '1rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                        onClick={() => setDeviceGroupListOpen(!deviceGroupListOpen)}
                                    >
                                        {deviceGroupListOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </button>
                                </td>
                            </tr>
                        </table>
                    </div>

                    <TextField
                        label=''
                        variant='outlined'
                        placeholder={t('searchPlaceholder')}
                        sx={{ width: '60%', marginTop: '1rem' }}
                        // onChange={(e) => setSearchString(e.target.value)}
                        InputProps={{
                            sx: { height: 40, borderRadius: '2rem' },
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <div
                        className='device__list'
                        style={{
                            width: '60%',
                            height: '20rem',
                            overflowY: 'auto',
                            marginTop: '1rem',
                            boxSizing: 'border-box',
                            border: '1px solid #DFE1E6',
                            borderRadius: '2rem',
                            background: '#FFFFFF',
                            padding: '1rem',
                            marginBottom: '1rem',
                        }}
                    >
                        {records.map((item: any, index: any) => (
                            <div
                                key={index}
                                className='device__item'
                                style={{
                                    width: '100%',
                                    height: '2.5rem',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0px 0px 8px 0px rgba(234, 233, 233, 0.80)',
                                    borderRadius: '62rem',
                                    position: 'relative',
                                    marginBottom: '0.5rem',
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: '1.25rem',
                                        fontFamily: 'Roboto',
                                        fontWeight: '400',
                                        color: '#53545C',
                                    }}
                                >
                                    {item.name}
                                </span>
                                <span
                                    style={{
                                        fontSize: '0.75rem',
                                        fontFamily: 'Roboto',
                                        fontWeight: '300',
                                        color: '#757575',
                                    }}
                                >
                                    ID: {item.deviceId}
                                </span>
                                <Checkbox
                                    style={{ position: 'absolute', right: '1rem', color: '#DFE1E6' }}
                                    // edge="end"
                                    onChange={handleToggle(item.deviceId)}
                                    checked={checked.includes(item.deviceId)}
                                    // inputProps={{ 'aria-labelledby': labelId }}
                                    icon={<CircleOutlinedIcon />}
                                    checkedIcon={<CircleIcon style={{ color: '#3F8EE8' }} />}
                                />
                            </div>
                        ))}
                    </div>
                </div>
                <div className='' style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'center' }}>
                    <button
                        className='export__button'
                        style={{ width: '60%', display: step !== 3 ? 'inline' : 'none' }}
                        onClick={handleNext}
                    >
                        {t('next')}
                    </button>
                    <button
                        className='export__button'
                        style={{ width: '120px', display: step === 3 ? 'inline' : 'none' }}
                        onClick={runAllExcel}
                    >
                        CSV
                    </button>
                    <button
                        className='export__button'
                        style={{ width: '120px', display: step === 3 ? 'inline' : 'none' }}
                        onClick={runAllPDF}
                    >
                        PDF
                    </button>
                </div>
                <Portal>
                    <Snackbar
                        open={preparing}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        sx={{
                            backgroundColor: '#333333',
                            color: '#FFFFFF',
                            padding: '0 1rem',
                        }}
                    >
                        <p>
                            <CircularProgress size='1rem' style={{ verticalAlign: 'text-top' }} /> Preparing to download{' '}
                            {exportName}
                        </p>
                    </Snackbar>
                </Portal>
                {rangeErrorSnackbar}
                {missingRangeSnackbar}
                {orderErrorSnackbar}
                <div
                    id='report'
                    ref={printRef}
                    style={{ display: 'block', width: '595pt', position: 'absolute', left: '-2000px' }}
                >
                    <Report reportData={reportData} />
                </div>
            </Box>
        </Modal>
    );
}

export default Export;
